import { type ButtonHTMLAttributes, type ReactNode } from 'react';

type ButtonVariant =
  | 'primary'   // amarelo JF, ações principais
  | 'danger'    // vermelho, ações destrutivas / emergência
  | 'info'      // azul (#78B4CF), encaminhamento ao técnico — fiel ao V8
  | 'success'   // verde sólido, conclusão/registro — fiel ao V8
  | 'ghost'     // outline muted, ações secundárias
  | 'green'     // confirmação operacional (outline sutil)
  | 'emergency' // pequeno, pulse — header
  | 'emg-big';  // grande, pulse — botão de pânico

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  full?: boolean;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-jf-yellow text-black hover:brightness-95',
  danger:
    'bg-level-emergencia text-white hover:brightness-110',
  info:
    'bg-[#78B4CF] text-black hover:brightness-95',
  success:
    'bg-level-normal text-black hover:brightness-95',
  ghost:
    'bg-transparent text-jf-muted border border-jf-border hover:border-jf-muted',
  green:
    'bg-level-normal/15 text-level-normal border border-level-normal/30 hover:bg-level-normal/25',
  emergency:
    'bg-level-emergencia text-white animate-pulse-emerg px-3 py-1.5 text-[11px] font-black whitespace-nowrap rounded-lg',
  'emg-big':
    'w-full text-white border-2 border-level-emergencia animate-pulse-emerg py-4 rounded-xl text-[17px] font-black tracking-wider ' +
    'bg-gradient-to-br from-level-emergencia to-red-900',
};

/**
 * Botão padrão. Equivale ao `.btn` + variantes do protótipo v2.
 * Tamanho base: padding 7px 14px, font 12px, bold, radius 8px.
 *
 * @example
 * <Button variant="primary">Salvar</Button>
 * <Button variant="emergency">Acionar Emergência</Button>
 */
export function Button({
  variant = 'primary',
  full = false,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  const isSpecial = variant === 'emergency' || variant === 'emg-big';
  // Estado desabilitado precisa ser VISÍVEL (antes só o atributo `disabled` era
  // setado, e um botão primário desabilitado continuava amarelo/ativo, parecendo
  // clicável). `disabled:` esmaece e neutraliza o hover.
  const disabledCls =
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:bg-inherit disabled:hover:border-inherit';
  const base = isSpecial
    ? `cursor-pointer transition ${disabledCls}`
    : `px-3.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition ${disabledCls}`;

  return (
    <button
      className={`${base} ${VARIANT_CLASSES[variant]} ${full ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
